import type { StoreHit } from '@/lib/algolia/stores';
import type { Weekday } from '@/lib/const';
import { getWeekdayFromDate, WEEKDAY_NAMES } from '@/lib/const';

/** Monday–Sunday display order derived from {@link WEEKDAY_NAMES} (Sunday-first). */
const DETAIL_WEEKDAY_ORDER: readonly Weekday[] = [
  ...WEEKDAY_NAMES.slice(1),
  WEEKDAY_NAMES[0],
] as const;

const WEEKDAY_LABELS: Record<Weekday, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

export type StoreHoursStatus = 'open' | 'closed' | 'temporary';

export interface StoreOpenStatus {
  status: StoreHoursStatus;
  hoursLabel: string;
  /** Weekday of the next opening — only set when status is 'closed'. */
  nextOpenWeekday?: Weekday;
}

interface TimeWithOffset {
  minutes: number;
  weekday: Weekday;
}

/**
 * Build ordered weekday schedule starting from today.
 *
 * Example Input:
 * hours = { monday: { openIntervals: [{ start: "10:00", end: "19:00" }] } }
 *
 * Example Output:
 * [{ weekday: "monday", label: "10:00 - 19:00" }, ...]
 */
function formatSchedule(hours: StoreHit['hours']): Array<{ weekday: Weekday; label: string }> {
  if (!hours) return [];

  const today = getWeekdayFromDate(new Date());
  const todayIndex = WEEKDAY_NAMES.indexOf(today);
  const orderedWeekdays = [
    ...WEEKDAY_NAMES.slice(todayIndex),
    ...WEEKDAY_NAMES.slice(0, todayIndex),
  ];

  return orderedWeekdays
    .map((weekday) => {
      const day = hours[weekday];
      if (!day || day.isClosed) {
        return { weekday, label: '' };
      }

      const intervals = day.openIntervals?.map((interval) => `${interval.start} - ${interval.end}`);
      return { weekday, label: intervals?.join(' / ') ?? '' };
    })
    .filter((entry) => Boolean(entry.label));
}

/**
 * Resolve local boutique time using the store UTC offset.
 *
 * Example Input:
 * utcOffset = "-05:00"
 *
 * Example Output:
 * { minutes: 600, weekday: "monday" }
 */
function getTimeWithOffset(utcOffset?: string): TimeWithOffset {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60_000;

  if (!utcOffset) {
    return {
      minutes: now.getHours() * 60 + now.getMinutes(),
      weekday: getWeekdayFromDate(now),
    };
  }

  const [hoursPart, minutesPart = '0'] = utcOffset.split(':');
  const offsetHours = Number.parseInt(hoursPart, 10);
  const offsetMinutes = Number.parseInt(minutesPart, 10);
  const shopTime = new Date(utc + (offsetHours * 60 + offsetMinutes) * 60_000);

  return {
    minutes: shopTime.getHours() * 60 + shopTime.getMinutes(),
    weekday: getWeekdayFromDate(shopTime),
  };
}

function isWithinOpenHours(
  localTime: TimeWithOffset,
  schedule: ReturnType<typeof formatSchedule>
): boolean {
  const today = schedule.find((entry) => entry.weekday === localTime.weekday);
  if (!today?.label) return false;

  const intervals = today.label.split(' / ');
  return intervals.some((interval) => {
    const [startRaw, endRaw] = interval.split(' - ');
    if (!startRaw || !endRaw) return false;

    const [startHour, startMinute] = startRaw.split(':').map(Number);
    const [endHour, endMinute] = endRaw.split(':').map(Number);
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    return localTime.minutes >= startMinutes && localTime.minutes <= endMinutes;
  });
}

/** Parse the last closing time (in minutes) from a label like "10:00 - 14:00 / 15:00 - 19:00". */
function getLastClosingMinutes(label: string): number {
  const intervals = label.split(' / ');
  let max = 0;
  for (const interval of intervals) {
    const endRaw = interval.split(' - ')[1];
    if (!endRaw) continue;
    const [h, m] = endRaw.split(':').map(Number);
    max = Math.max(max, h * 60 + m);
  }
  return max;
}

/**
 * Resolve open/closed status and hours label for a boutique card.
 *
 * Example Input:
 * store = { hours: { monday: { openIntervals: [{ start: "10:00", end: "19:00" }] } }, utc_offset: "-05:00" }
 *
 * Example Output:
 * { status: "open", hoursLabel: "10:00 - 19:00" }
 */
export function getStoreOpenStatus(store: StoreHit): StoreOpenStatus {
  const schedule = formatSchedule(store.hours);

  if (schedule.length === 0) {
    return { status: 'temporary', hoursLabel: '' };
  }

  const localTime = getTimeWithOffset(store.utc_offset);
  const today = schedule.find((entry) => entry.weekday === localTime.weekday);
  const todayLabel = today?.label ?? schedule[0]?.label ?? '';

  if (isWithinOpenHours(localTime, schedule)) {
    return { status: 'open', hoursLabel: todayLabel };
  }

  // Only skip today if we're past its last closing time; before opening we still want today.
  const isPastTodayHours = today?.label
    ? localTime.minutes > getLastClosingMinutes(today.label)
    : true;

  const nextOpen = isPastTodayHours
    ? (schedule.find((entry) => entry.weekday !== localTime.weekday && Boolean(entry.label)) ??
      schedule.find((entry) => Boolean(entry.label)))
    : today;

  return {
    status: 'closed',
    hoursLabel: nextOpen?.label ?? todayLabel,
    nextOpenWeekday: nextOpen?.weekday,
  };
}

/**
 * Format a weekday label for closed-state messaging.
 *
 * Example Input:
 * weekday = "monday"
 *
 * Example Output:
 * "Monday"
 */
export function formatWeekdayLabel(weekday: Weekday): string {
  return WEEKDAY_LABELS[weekday];
}

export interface StoreWeekdayHours {
  weekday: Weekday;
  hoursLabel: string;
  isClosed: boolean;
}

/**
 * Build a Monday–Sunday schedule for the boutique detail page.
 *
 * Example Input:
 * hours = { monday: { openIntervals: [{ start: "10:00", end: "19:00" }] } }
 *
 * Example Output:
 * [{ weekday: "monday", hoursLabel: "10:00 - 19:00", isClosed: false }, ...]
 */
export function getStoreWeekSchedule(hours: StoreHit['hours']): StoreWeekdayHours[] {
  if (!hours) return [];

  return DETAIL_WEEKDAY_ORDER.map((weekday) => {
    const day = hours[weekday];
    if (!day || day.isClosed) {
      return { weekday, hoursLabel: '', isClosed: true };
    }

    const intervals = day.openIntervals?.map((interval) => `${interval.start} - ${interval.end}`);
    const hoursLabel = intervals?.join(' / ') ?? '';

    return {
      weekday,
      hoursLabel,
      isClosed: !hoursLabel,
    };
  });
}

export interface GroupedStoreHours {
  startWeekday: Weekday;
  endWeekday: Weekday;
  hoursLabel: string;
}

function areAdjacentWeekdays(a: Weekday, b: Weekday): boolean {
  const aIndex = DETAIL_WEEKDAY_ORDER.indexOf(a);
  const bIndex = DETAIL_WEEKDAY_ORDER.indexOf(b);
  return bIndex === aIndex + 1;
}

/**
 * Group consecutive weekdays that share the same opening hours (detail page display).
 *
 * Example Input:
 * hours with Mon–Sat 10:00–19:00 and Sun 11:00–19:00
 *
 * Example Output:
 * [
 *   { startWeekday: "monday", endWeekday: "saturday", hoursLabel: "10:00 - 19:00" },
 *   { startWeekday: "sunday", endWeekday: "sunday", hoursLabel: "11:00 - 19:00" },
 * ]
 */
export function getGroupedStoreWeekSchedule(hours: StoreHit['hours']): GroupedStoreHours[] {
  const openDays = getStoreWeekSchedule(hours).filter(
    (entry) => !entry.isClosed && entry.hoursLabel
  );

  if (openDays.length === 0) return [];

  const groups: GroupedStoreHours[] = [];
  let current: GroupedStoreHours = {
    startWeekday: openDays[0].weekday,
    endWeekday: openDays[0].weekday,
    hoursLabel: openDays[0].hoursLabel,
  };

  for (let index = 1; index < openDays.length; index += 1) {
    const entry = openDays[index];
    if (
      entry.hoursLabel === current.hoursLabel &&
      areAdjacentWeekdays(current.endWeekday, entry.weekday)
    ) {
      current = { ...current, endWeekday: entry.weekday };
    } else {
      groups.push(current);
      current = {
        startWeekday: entry.weekday,
        endWeekday: entry.weekday,
        hoursLabel: entry.hoursLabel,
      };
    }
  }

  groups.push(current);
  return groups;
}

/**
 * Format hours label for display (hyphen → en dash).
 *
 * Example Input:
 * "10:00 - 19:00"
 *
 * Example Output:
 * "10:00 – 19:00"
 */
export function formatHoursDisplayLabel(hoursLabel: string): string {
  return hoursLabel.replaceAll(' - ', ' – ');
}
